import { MediaType } from '@/types';
import getMediaTypeFromString from '@/utils/generation/getMediaTypeFromString';

import type { FlowMedia } from '@/types';
import type { PollResponse } from '@/requests/magic-flow/pollRequest';

export interface BuiltFlowMedia {
  resultFile: FlowMedia;
  resultType: string;
  isVideo: boolean;
  isAudio: boolean;
}

/**
 * Pure helper that turns a poll response into the `FlowMedia` shape used
 * by the rest of the app, plus a couple of derived flags. Throws when the
 * response has no result URL — caller treats that as a generation failure.
 */
const buildFlowMedia = ({
  generationId,
  pollingResponse,
}: {
  generationId: string;
  pollingResponse: PollResponse;
}): BuiltFlowMedia => {
  // Get result from polling response
  const result = pollingResponse.response.result?.[0];
  const resultUrl = result?.result?.url || pollingResponse.response.output?.url;

  if (!resultUrl) {
    throw new Error('No result URL returned from generation');
  }

  const resultType = result?.type?.toLowerCase() ?? '';
  const mediaType = getMediaTypeFromString(resultType);
  const isVideo = mediaType === MediaType.VIDEO;
  const isAudio = mediaType === MediaType.AUDIO;
  const posterUrl = isVideo ? result?.result?.frames?.first : undefined;

  const resultFile: FlowMedia = {
    id: generationId,
    url: resultUrl,
    width: result?.result?.metadata?.width,
    height: result?.result?.metadata?.height,
    type: mediaType,
    driveUid: result?.driveUid,
    poster: posterUrl,
  };

  return { resultFile, resultType, isVideo, isAudio };
};

export default buildFlowMedia;
